import React, { useEffect, useState } from "react";
import { Chart as ChartJS, LinearScale, Legend } from "chart.js/auto";
import { Line } from "react-chartjs-2";
import axios from "axios";

const AnalyticsLineChart = () => {
  ChartJS.register(LinearScale, Legend);

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const [events, setEvents] = useState([]);
  const [onlineCounts, setOnlineCounts] = useState([]);
  const [offlineCounts, setOfflineCounts] = useState([]);
  const [yearFilter, setYearFilter] = useState("last12");
  const [yearOptions, setYearOptions] = useState([]);
  const [dynamicLabels, setDynamicLabels] = useState(monthNames);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isLast12 = yearFilter === "last12";

  const getFilterRange = () => {
    const now = new Date();
    if (isLast12) {
      const to = new Date();
      const from = new Date(to);
      from.setMonth(to.getMonth() - 11);
      from.setDate(1);
      return { from, to };
    } else {
      const from = new Date(`${yearFilter}-01-01`);
      const to = new Date(`${yearFilter}-12-31`);
      return { from, to };
    }
  };

  const generateLast12MonthLabels = () => {
    const now = new Date();
    const labels = [];
    for (let i = 11; i >= 0; i--) {
      const date = new Date();
      date.setMonth(now.getMonth() - i);
      labels.push(monthNames[date.getMonth()]);
    }
    return labels;
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/business/events`,
          { withCredentials: true }
        );
        const fetchedEvents = res?.data.data || [];
        setEvents(fetchedEvents);


        const yearsSet = new Set();
        fetchedEvents.forEach(event => {
          const year = new Date(event.eventDate).getFullYear();
          yearsSet.add(year);
        });

        const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
        setYearOptions(["last12", ...sortedYears.map(String)]);
      } catch (err) {
        console.error("Failed to fetch events:", err);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const { from, to } = getFilterRange();
    const tempOnline = new Array(12).fill(0);
    const tempOffline = new Array(12).fill(0);

    events.forEach((event) => {
      const eventDate = new Date(event.eventDate);
      if (eventDate >= from && eventDate <= to) {
        const monthIndex = eventDate.getMonth();
        if (isLast12) {
          const diffInMonths =
            (eventDate.getFullYear() - from.getFullYear()) * 12 +
            eventDate.getMonth() -
            from.getMonth();
          if (diffInMonths >= 0 && diffInMonths < 12) {
            if (event.type === "automatic") tempOnline[diffInMonths]++;
            else if (event.type === "manual") tempOffline[diffInMonths]++;
          }
        } else {
          if (event.type === "automatic") tempOnline[monthIndex]++;
          else if (event.type === "manual") tempOffline[monthIndex]++;
        }
      }
    });

    if (isLast12) {
      setDynamicLabels(generateLast12MonthLabels());
    } else {
      setDynamicLabels(monthNames);
    }

    setOnlineCounts(tempOnline);
    setOfflineCounts(tempOffline);
  }, [yearFilter, events]);

  const data = {
    labels: dynamicLabels,
    datasets: [
      {
        label: "Online",
        data: onlineCounts,
        backgroundColor: "#21A62C",
        borderColor: "#21A62C",
      },
      {
        label: "Offline",
        data: offlineCounts,
        backgroundColor: "#260F99",
        borderColor: "#F99600",
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        align: "center",
        labels: {
          padding: 30,
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        type: "category",
        position: "bottom",
      },
      y: {
        beginAtZero: true,
        type: "linear",
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div style={{
      height: "100%",
      backgroundColor: "#FFFFFF",
      padding: "3%"
    }}>
      {/* Header with title and dropdown */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1rem 2% 1.5rem 0",
      }}>
        <h2 style={{
          fontSize: "24px",
          fontFamily: "Poppins",
          fontWeight: "600",
          color: "#1C1154",
          margin: 0,
        }}>
          Bookings
        </h2>

        <div style={{ position: "relative", width: "160px" }}>
          <div
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              backgroundColor: "#fff",
              // border: "1px solid #ccc",
              // borderRadius: "8px",
              padding: "10px 14px",
              paddingRight: "36px",
              cursor: "pointer",
              fontFamily: "Poppins",
              fontSize: "14px",
              color: "#625C73",
              // boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
              // position: "relative",
              userSelect: "none",
            }}
          >
            {yearFilter === "last12" ? "Last 12 Months" : `Year ${yearFilter}`}
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              style={{
                position: "absolute",
                right: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="#625C73"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {dropdownOpen && (
            <div style={{
              position: "absolute",
              top: "105%",
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              zIndex: 10,
              padding: "6px 0",
            }}>
              {yearOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => {
                    setYearFilter(option);
                    setDropdownOpen(false);
                  }}
                  style={{
                    padding: "10px 14px",
                    cursor: "pointer",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    color: "#625C73",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#F3F3F3")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  {option === "last12" ? "Last 12 Months" : `Year ${option}`}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <Line data={data} options={options} />
    </div>
  );
};

export default AnalyticsLineChart;
